import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { authAPI } from '../services/authAPI';
import { backgroundCheckAPI } from '../services/backgroundCheckAPI';
import { formatAPIError } from '../utils/validation';
import Header from '../components/layout/Header';
import { useNavigate, Link } from 'react-router-dom';
import './styles/Dashboard.css';

const AgencyDashboard = () => {
  const { user, logout, updateUser } = useAuth();
  const navigate = useNavigate();

  // State management
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showMFASettings, setShowMFASettings] = useState(false); 

  // Profile data state
  const [profileData, setProfileData] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(true);

  // Assignments state
  const [assignments, setAssignments] = useState([]);
  const [loadingAssignments, setLoadingAssignments] = useState(true);
  const [assignmentsError, setAssignmentsError] = useState('');

  // Modal states
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [selectedCheck, setSelectedCheck] = useState(null);

  // Status update form
  const [statusFormData, setStatusFormData] = useState({
    status: '',
    comments: ''
  });

  // Report upload form
  const [reportFormData, setReportFormData] = useState({
    reportUrl: '',
    result: 'PASS',
    comments: '',
    disclaimerAccepted: false
  });

  // Filter state
  const [filterStatus, setFilterStatus] = useState('');

  // MFA Settings State
  const [mfaSettings, setMfaSettings] = useState({
    mfaEnabled: user?.mfaEnabled || false,
    mfaType: user?.mfaType || 'email'
  });
  const [tempSettings, setTempSettings] = useState({ ...mfaSettings });

  // Available statuses for agency
  const availableStatuses = [
    { value: 'IN_PROGRESS', label: 'In Progress' },
    { value: 'ON_HOLD', label: 'On Hold' },
    { value: 'COMPLETED', label: 'Completed' },
    { value: 'UNABLE_TO_VERIFY', label: 'Unable to Verify' }
  ];

  // Result options
  const resultOptions = [
    { value: 'PASS', label: 'Pass' },
    { value: 'FAIL', label: 'Fail' },
    { value: 'CONDITIONAL', label: 'Conditional' }
  ];

  // ==================== LOAD PROFILE DATA ====================
  const loadProfileData = useCallback(async () => {
  try {
    setLoadingProfile(true);
    console.log('🏢 AgencyDashboard: Loading profile data...');

    const response = await authAPI.getProfile();
    console.log('📊 Profile data:', response.data);
    setProfileData(response.data);

    if (response.data) {
      const updatedUserData = {
        ...user,
        firstName: response.data.firstName || user?.firstName,
        lastName: response.data.lastName || user?.lastName,
        email: response.data.account?.email || user?.email,
        phone: response.data.account?.phone || user?.phone,
      };
      updateUser(updatedUserData);
    }
  } catch (error) {
    console.error('❌ Failed to load profile:', error);
    setProfileData(null);
  } finally {
    setLoadingProfile(false);
  }
}, [user, updateUser]);

useEffect(() => {
  loadProfileData();
}, [loadProfileData]);

  // ==================== LOAD ASSIGNMENTS ====================
  const loadAssignments = useCallback(async () => {
  try {
    setLoadingAssignments(true);
    setAssignmentsError('');
    console.log('🔍 Loading agency assignments...');

    const result = await backgroundCheckAPI.getAgencyAssignments();

    let assignmentsArray = Array.isArray(result)
      ? result
      : Array.isArray(result?.data)
        ? result.data
        : [];

    if (filterStatus) {
      assignmentsArray = assignmentsArray.filter((a) => a.status === filterStatus);
    }

    setAssignments(assignmentsArray);
    console.log('✅ Assignments loaded:', assignmentsArray.length);
  } catch (error) {
    console.error('❌ Failed to load assignments:', error);
    setAssignmentsError(formatAPIError(error));
    setAssignments([]);
  } finally {
    setLoadingAssignments(false);
  }
}, [filterStatus]);

useEffect(() => {
  loadAssignments();
}, [loadAssignments]);

  // Update MFA settings when user changes
  useEffect(() => {
    if (user) {
      setMfaSettings({
        mfaEnabled: user.mfaEnabled || false,
        mfaType: user.mfaType || 'email'
      });
      setTempSettings({
        mfaEnabled: user.mfaEnabled || false,
        mfaType: user.mfaType || 'email'
      });
    }
  }, [user]);

  // ==================== MFA HANDLERS ====================
  const handleMFAToggle = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const newMfaEnabled = !mfaSettings.mfaEnabled;
      await authAPI.updateMFA({
        mfaEnabled: newMfaEnabled,
        mfaType: tempSettings.mfaType
      });

      setMfaSettings({
        mfaEnabled: newMfaEnabled,
        mfaType: tempSettings.mfaType
      });

      setSuccess(
        newMfaEnabled
          ? `🎉 Two-Factor Authentication enabled via ${tempSettings.mfaType}!`
          : '✅ Two-Factor Authentication disabled successfully!'
      );

      setTimeout(() => setSuccess(''), 5000);
    } catch (error) {
      console.error('❌ MFA update failed:', error);
      setError(formatAPIError(error));
    } finally {
      setLoading(false);
    }
  };

  const handleMfaTypeChange = (newType) => {
    setTempSettings(prev => ({ ...prev, mfaType: newType }));
  };

  // ==================== HELPER FUNCTIONS ====================
  const getDisplayName = () => {
    if (loadingProfile) return 'Loading...';
    const firstName = profileData?.firstName || user?.firstName;
    const lastName = profileData?.lastName || user?.lastName;
    if (firstName && lastName) {
      return `${firstName} ${lastName}`;
    }
    return user?.email || 'Agency';
  };

  const getDisplayEmail = () => {
    return profileData?.account?.email || user?.email || 'Not provided';
  };

  const getDisplayPhone = () => {
    return profileData?.account?.phone || user?.phone || 'Not provided';
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      PENDING_CONSENT: { label: 'Pending Consent', class: 'pending' },
      ASSIGNED: { label: 'Assigned', class: 'pending' },
      IN_PROGRESS: { label: 'In Progress', class: 'pending' },
      COMPLETED: { label: 'Completed', class: 'verified' },
      ON_HOLD: { label: 'On Hold', class: 'not-requested' },
      UNABLE_TO_VERIFY: { label: 'Unable to Verify', class: 'rejected' },
    };
    const statusInfo = statusMap[status] || { label: status || 'Unknown', class: 'not-requested' };
    return statusInfo.label;  // just the label text
  };

  const getResultBadge = (result) => {
    if (!result) return 'Pending';  // fallback text
    const resultMap = {
      PASS: { label: '✓ Pass', class: 'validated' },
      FAIL: { label: '✗ Fail', class: 'rejected' },
      CONDITIONAL: { label: '⚠ Conditional', class: 'pending' },
    };
    const resultInfo = resultMap[result] || { label: result, class: 'not-requested' };
    return resultInfo.label;  // just the label text
  };


  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getStats = () => {
    const total = assignments.length;
    const assigned = assignments.filter(a => a.status === 'ASSIGNED').length;
    const inProgress = assignments.filter(a => a.status === 'IN_PROGRESS').length;
    const completed = assignments.filter(a => a.status === 'COMPLETED').length;

    return { total, assigned, inProgress, completed };
  };

  // ==================== HANDLERS ====================
  const handleOpenStatusModal = (check) => {
    setSelectedCheck(check);
    setStatusFormData({
      status: check.status,
      comments: ''
    });
    setShowStatusModal(true);
  };

  const handleOpenReportModal = (check) => {
    setSelectedCheck(check);
    setReportFormData({
      reportUrl: '',
      result: 'PASS',
      comments: '',
      disclaimerAccepted: false
    });
    setShowReportModal(true);
  };

  const handleUpdateStatus = async (e) => {
    e.preventDefault();

    try {
      setLoading(true);
      setError('');

      console.log('📤 Updating status for check:', selectedCheck.id);

      await backgroundCheckAPI.updateStatus(
        selectedCheck.id,
        statusFormData.status,
        statusFormData.comments
      );

      setSuccess('Status updated successfully!');
      setShowStatusModal(false);
      setSelectedCheck(null);

      // Reload assignments
      loadAssignments();

      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('❌ Failed to update status:', error);
      setError(formatAPIError(error));
    } finally {
      setLoading(false);
    }
  };

  const handleUploadReport = async (e) => {
    e.preventDefault();

    if (!reportFormData.disclaimerAccepted) {
      setError('You must accept the disclaimer to submit the report');
      return;
    }

    try {
      setLoading(true);
      setError('');

      console.log('📤 Uploading report for check:', selectedCheck.id);

      await backgroundCheckAPI.uploadReport(
        selectedCheck.id,
        reportFormData.reportUrl,
        reportFormData.result,
        reportFormData.comments
      );

      setSuccess('Report uploaded successfully!');
      setShowReportModal(false);
      setSelectedCheck(null);

      // Reload assignments
      loadAssignments();

      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('❌ Failed to upload report:', error);
      setError(formatAPIError(error));
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const stats = getStats();

  // ==================== RENDER ====================
  return (
    <>
      <Header />

      {/* Role Indicator Banner */}
      <div style={{
        padding: '12px 20px',
        background: '#f3f4f6',
        borderBottom: '1px solid #ddd',
        textAlign: 'center',
        fontSize: '14px',
        fontWeight: '500',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px'
      }}>
        <span style={{ fontSize: '20px' }}>🏢</span>
        <span>Agency Dashboard</span>
        <span style={{ color: '#666', fontWeight: 'normal' }}>•</span>
        <span style={{ color: '#666', fontWeight: 'normal' }}>Manage verification assignments and submit reports</span>
      </div>

      <div className="dashboard-container" style={{ padding: '2rem', maxWidth: '1400px', margin: '0 auto' }}>

        {/* Welcome Section */}
        <div className="dashboard-header" style={{ marginBottom: '2rem' }}>
          <h1 style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>Welcome back, {getDisplayName()}!</h1>
          <p style={{ color: '#666', fontSize: '1rem' }}>Manage verification assignments and submit background check reports</p>
        </div>

        {/* Success/Error Messages */}
        {success && (
          <div className="alert alert-success" style={{ marginBottom: '1rem' }}>
            {success}
          </div>
        )}
        {error && (
          <div className="alert alert-error" style={{ marginBottom: '1rem' }}>
            {error}
          </div>
        )}

        {/* Stats Overview */}
        <div className="dashboard-overview">
          <div className="stats-grid">
            {/* Total Assignments */}
            <div className="stat-card total">
              <div className="stat-icon">📋</div>
              <div className="stat-content">
                <h3>{stats.total}</h3>
                <p>Total Assignments</p>
                <small>All time</small>
              </div>
            </div>

            {/* Assigned */}
            <div className="stat-card pending">
              <div className="stat-icon">⏳</div>
              <div className="stat-content">
                <h3>{stats.assigned}</h3>
                <p>New Assignments</p>
                <small>Awaiting action</small>
              </div>
            </div>

            {/* In Progress */}
            <div className="stat-card pending">
              <div className="stat-icon">🔄</div>
              <div className="stat-content">
                <h3>{stats.inProgress}</h3>
                <p>In Progress</p>
                <small>Currently working</small>
              </div>
            </div>

            {/* Completed */}
            <div className="stat-card verified">
              <div className="stat-icon">✅</div>
              <div className="stat-content">
                <h3>{stats.completed}</h3>
                <p>Completed</p>
                <small>Reports submitted</small>
              </div>
            </div>
          </div>
        </div>

        {/* Main Dashboard Content */}
        <div className="dashboard-content">

          {/* Main Content Area */}
          <div className="dashboard-main">

            {/* Assignments Table */}
            <div className="dashboard-card">
              <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h2>📋 My Assignments</h2>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                  <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    style={{
                      padding: '0.5rem',
                      borderRadius: '6px',
                      border: '1px solid #ddd',
                      fontSize: '0.875rem'
                    }}
                  >
                    <option value="">All Status</option>
                    <option value="ASSIGNED">Assigned</option>
                    <option value="IN_PROGRESS">In Progress</option>
                    <option value="ON_HOLD">On Hold</option>
                    <option value="COMPLETED">Completed</option>
                  </select>
                </div>
              </div>

              {loadingAssignments ? (
                <div className="loading-state">
                  <div className="spinner"></div>
                  <p>Loading assignments...</p>
                </div>
              ) : assignmentsError ? (
                <div className="error-state">
                  <p>{assignmentsError}</p>
                  <p>Unable to load assignments</p>
                </div>
              ) : assignments.length > 0 ? (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                    <thead>
                      <tr style={{ borderBottom: '2px solid #e5e7eb', textAlign: 'left' }}>
                        <th style={{ padding: '1rem', fontWeight: '600' }}>Candidate</th>
                        <th style={{ padding: '1rem', fontWeight: '600' }}>Employer</th>
                        <th style={{ padding: '1rem', fontWeight: '600' }}>Scope</th>
                        <th style={{ padding: '1rem', fontWeight: '600' }}>Status</th>
                        <th style={{ padding: '1rem', fontWeight: '600' }}>Result</th>
                        <th style={{ padding: '1rem', fontWeight: '600' }}>Assigned</th>
                        <th style={{ padding: '1rem', fontWeight: '600' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {assignments.map((assignment) => {
                        const candidate = assignment.candidate;
                        const employer = assignment.employer;

                        const candidateName =
                          candidate?.profile?.firstName || candidate?.profile?.lastName
                            ? `${candidate.profile.firstName || ''} ${candidate.profile.lastName || ''
                              }`.trim()
                            : 'N/A';

                        const candidateEmail = candidate?.email || 'N/A';

                        const employerName =
                          employer?.profile?.firstName || employer?.profile?.lastName
                            ? `${employer.profile.firstName || ''} ${employer.profile.lastName || ''
                              }`.trim()
                            : 'N/A';

                        const employerEmail = employer?.email || 'N/A';

                        return (
                          <tr key={assignment.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                            {/* Candidate */}
                            <td style={{ padding: '1rem' }}>
                              <div>
                                <div style={{ fontWeight: '500' }}>{candidateName}</div>
                                <div style={{ fontSize: '0.75rem', color: '#666' }}>
                                  {candidateEmail}
                                </div>
                              </div>
                            </td>

                            {/* Employer */}
                            <td style={{ padding: '1rem' }}>
                              <div>
                                <div style={{ fontWeight: '500' }}>{employerName}</div>
                                <div style={{ fontSize: '0.75rem', color: '#666' }}>
                                  {employerEmail}
                                </div>
                              </div>
                            </td>

                            {/* Scope / Status / Result / Assigned / Actions */}
                            <td style={{ padding: '1rem' }}>
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
                                {assignment.scope?.map((scope, idx) => (
                                  <span
                                    key={idx}
                                    style={{
                                      padding: '0.25rem 0.5rem',
                                      background: '#f3f4f6',
                                      borderRadius: '4px',
                                      fontSize: '0.75rem',
                                    }}
                                  >
                                    {scope}
                                  </span>
                                ))}
                              </div>
                            </td>
                            <td style={{ padding: '1rem' }}>{getStatusBadge(assignment.status)}</td>
                            <td style={{ padding: '1rem' }}>{getResultBadge(assignment.result)}</td>
                            <td style={{ padding: '1rem', color: '#666' }}>
                              {formatDate(assignment.createdAt)}
                            </td>
                            <td style={{ padding: '1rem' }}>
                              <div style={{ display: 'flex', gap: '0.5rem' }}>
                                {assignment.status !== 'COMPLETED' && (
                                  <button
                                    onClick={() => {
                                      console.log('clicked assignment', assignment.id);
                                      handleOpenStatusModal(assignment);
                                    }}

                                    style={{
                                      padding: '0.375rem 0.75rem',
                                      background: '#3b82f6',
                                      color: 'white',
                                      border: 'none',
                                      borderRadius: '4px',
                                      fontSize: '0.75rem',
                                      cursor: 'pointer',
                                    }}
                                  >
                                    Update Status
                                  </button>
                                )}
                                {assignment.status === 'IN_PROGRESS' && !assignment.reportUrl && (
                                  <button
                                    onClick={() => handleOpenReportModal(assignment)}
                                    style={{
                                      padding: '0.375rem 0.75rem',
                                      background: '#10b981',
                                      color: 'white',
                                      border: 'none',
                                      borderRadius: '4px',
                                      fontSize: '0.75rem',
                                      cursor: 'pointer',
                                    }}
                                  >
                                    Upload Report
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}



                    </tbody>
                  </table>
                  {/* Status Update Modal */}
                  {showStatusModal && selectedCheck && (
                    <div className="dashboard-modal-overlay" onClick={() => setShowStatusModal(false)}>
                      <div
                        className="dashboard-modal-content"
                        style={{ maxWidth: '520px' }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="dashboard-modal-header">
                          <h2>Update Background Check Status</h2>
                          <button
                            className="dashboard-close-btn"
                            onClick={() => setShowStatusModal(false)}
                          >
                            ×
                          </button>
                        </div>

                        <div className="dashboard-modal-body">
                          <p style={{ marginBottom: '0.75rem', fontSize: '0.875rem', color: '#666' }}>
                            <strong>Candidate:</strong>{' '}
                            {selectedCheck.candidate?.profile?.firstName}{' '}
                            {selectedCheck.candidate?.profile?.lastName} (
                            {selectedCheck.candidate?.email})
                          </p>
                          <p style={{ marginBottom: '1.25rem', fontSize: '0.875rem', color: '#666' }}>
                            <strong>Scope:</strong> {selectedCheck.scope?.join(', ')}
                          </p>

                          <form onSubmit={handleUpdateStatus}>
                            <div className="form-group">
                              <label className="form-label">Status *</label>
                              <select
                                className="form-control"
                                value={statusFormData.status}
                                onChange={(e) =>
                                  setStatusFormData((prev) => ({ ...prev, status: e.target.value }))
                                }
                                required
                              >
                                <option value="">Choose status...</option>
                                {availableStatuses.map((s) => (
                                  <option key={s.value} value={s.value}>
                                    {s.label}
                                  </option>
                                ))}
                              </select>
                            </div>

                            <div className="form-group">
                              <label className="form-label">Comments (optional)</label>
                              <textarea
                                className="form-control"
                                rows={3}
                                value={statusFormData.comments}
                                onChange={(e) =>
                                  setStatusFormData((prev) => ({ ...prev, comments: e.target.value }))
                                }
                              />
                            </div>

                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
                              <button
                                type="button"
                                className="btn-secondary"
                                onClick={() => setShowStatusModal(false)}
                              >
                                Cancel
                              </button>
                              <button type="submit" className="btn-primary" disabled={loading}>
                                {loading ? 'Updating...' : 'Save Status'}
                              </button>
                            </div>
                          </form>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="empty-state">
                  <p>No assignments yet</p>
                  <p style={{ fontSize: '0.875rem', color: '#666' }}>
                    Employers will assign background checks to your agency
                  </p>
                </div>
              )}
            </div>

          </div>

          {/* Sidebar */}
          <div className="dashboard-sidebar">

            {/* Profile Info Card with Edit Profile */}
            <div className="dashboard-card">
              <div className="card-header">
                <h3>👤 Profile Info</h3>
                <Link
                  to="/profile"
                  style={{
                    fontSize: '0.875rem',
                    color: '#3b82f6',
                    textDecoration: 'none',
                    fontWeight: '500'
                  }}
                >
                  Edit Profile
                </Link>
              </div>
              <div className="profile-info">
                <div className="info-row">
                  <span className="info-label">Name:</span>
                  <span className="info-value">{getDisplayName()}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Email:</span>
                  <span className="info-value">{getDisplayEmail()}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Phone:</span>
                  <span className="info-value">{getDisplayPhone()}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Role:</span>
                  <span className="verification-badge validated">Agency</span>
                </div>
              </div>
            </div>

            {/* MFA Settings Card */}
            <div className="dashboard-card">
              <div className="card-header">
                <h3>🔒 Security Settings</h3>
              </div>
              <div className="mfa-settings">
                <div className="mfa-status">
                  <div className="mfa-icon">
                    {mfaSettings.mfaEnabled ? '🔒' : '🔓'}
                  </div>
                  <div className="mfa-info">
                    <h4>Two-Factor Authentication</h4>
                    <p style={{ fontSize: '0.875rem', color: '#666', margin: '0.25rem 0 0 0' }}>
                      {mfaSettings.mfaEnabled
                        ? `Enabled via ${mfaSettings.mfaType}`
                        : 'Add an extra layer of security to your account'}
                    </p>
                  </div>
                </div>
                <button
                  className="btn-primary"
                  onClick={() => setShowMFASettings(true)}
                  style={{
                    width: '100%',
                    marginTop: '1rem',
                    padding: '0.75rem',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    cursor: 'pointer'
                  }}
                >
                  {mfaSettings.mfaEnabled ? 'Manage MFA' : 'Enable MFA'}
                </button>
              </div>
            </div>

            {/* Performance Stats */}
            <div className="dashboard-card">
              <div className="card-header">
                <h3>📊 Performance</h3>
              </div>
              <div style={{ padding: '1rem' }}>
                <div style={{ marginBottom: '0.75rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
                    <span style={{ fontSize: '0.875rem', fontWeight: '500' }}>Completion Rate</span>
                    <span style={{ fontSize: '0.875rem', fontWeight: '600', color: '#3b82f6' }}>
                      {stats.total > 0 ? Math.round((stats.completed / stats.total) * 100) : 0}%
                    </span>
                  </div>
                  <div style={{
                    width: '100%',
                    height: '8px',
                    background: '#e5e7eb',
                    borderRadius: '9999px',
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      width: `${stats.total > 0 ? (stats.completed / stats.total) * 100 : 0}%`,
                      height: '100%',
                      background: 'linear-gradient(90deg, #3b82f6, #2563eb)',
                      transition: 'width 0.3s ease'
                    }} />
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Actions Card */}
            <div className="dashboard-card">
              <div className="card-header">
                <h3>⚡ Quick Actions</h3>
              </div>
              <div className="action-buttons">
                <button
                  onClick={() => setFilterStatus('ASSIGNED')}
                  className="action-btn"
                >
                  <span className="action-icon">📋</span>
                  <span>View New Assignments</span>
                </button>
                <button
                  onClick={() => setFilterStatus('IN_PROGRESS')}
                  className="action-btn"
                >
                  <span className="action-icon">🔄</span>
                  <span>View In Progress</span>
                </button>
                <Link to="/change-password" className="action-btn">
                  <span className="action-icon">🔒</span>
                  <span>Change Password</span>
                </Link>
                <button onClick={handleLogout} className="action-btn logout-btn">
                  <span className="action-icon">🚪</span>
                  <span>Logout</span>
                </button>
              </div>
            </div>

          </div>
        </div>

      </div>

      {/* MFA Settings Modal */}
      {showMFASettings && (
        <div className="dashboard-modal-overlay" onClick={() => setShowMFASettings(false)}>
          <div className="dashboard-modal-content mfa-simple-modal" onClick={(e) => e.stopPropagation()}>
            <div className="dashboard-modal-header">
              <h2>Configure Two-Factor Authentication</h2>
              <button className="dashboard-close-btn" onClick={() => setShowMFASettings(false)}>×</button>
            </div>

            <div className="dashboard-modal-body">
              {/* MFA Type Selection */}
              <div className="mfa-type-options-simple">
                <label className={`mfa-type-card ${tempSettings.mfaType === 'email' ? 'selected' : ''}`}>
                  <input
                    type="radio"
                    name="mfaType"
                    value="email"
                    checked={tempSettings.mfaType === 'email'}
                    onChange={() => handleMfaTypeChange('email')}
                  />
                  <div className="card-content">
                    <div className="card-icon">📧</div>
                    <div className="card-text">
                      <h4>Email Verification</h4>
                      <p>Send verification codes to your email</p>
                    </div>
                  </div>
                </label>

                <label className={`mfa-type-card ${tempSettings.mfaType === 'sms' ? 'selected' : ''}`}>
                  <input
                    type="radio"
                    name="mfaType"
                    value="sms"
                    checked={tempSettings.mfaType === 'sms'}
                    onChange={() => handleMfaTypeChange('sms')}
                  />
                  <div className="card-content">
                    <div className="card-icon">📱</div>
                    <div className="card-text">
                      <h4>SMS Verification</h4>
                      <p>Send verification codes to your phone</p>
                    </div>
                  </div>
                </label>
              </div>

              {/* Enable/Disable MFA Button */}
              <button
                className="btn-enable-mfa"
                onClick={handleMFAToggle}
                disabled={loading}
              >
                <span className="btn-icon">🔒</span>
                {loading ? 'Processing...' : mfaSettings.mfaEnabled ? 'Update MFA' : 'Enable MFA'}
              </button>

              {/* Optional: Disable MFA Button */}
              {mfaSettings.mfaEnabled && (
                <button
                  className="btn-disable-mfa"
                  onClick={handleMFAToggle}
                  disabled={loading}
                >
                  Disable MFA
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Status Update Modal */}
      {showStatusModal && selectedCheck && (
        <div className="dashbard-modal-overlay" onClick={() => setShowStatusModal(false)}>
          <div className="dashbard-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="dashbard-modal-header">
              <h2>Update Assignment Status</h2>
              <button className="dashbard-close-btn" onClick={() => setShowStatusModal(false)}>×</button>
            </div>

            <div className="dashbard-modal-body">
              <div style={{ marginBottom: '1rem', padding: '1rem', background: '#f9fafb', borderRadius: '8px' }}>
                <p style={{ margin: 0, fontSize: '0.875rem', color: '#666' }}>
                  <strong>Candidate:</strong> {selectedCheck.candidate?.firstName} {selectedCheck.candidate?.lastName}
                </p>
              </div>

              <form onSubmit={handleUpdateStatus}>
                <div className="form-group">
                  <label className="form-label">New Status *</label>
                  <select
                    className="form-control"
                    value={statusFormData.status}
                    onChange={(e) => setStatusFormData({ ...statusFormData, status: e.target.value })}
                    required
                  >
                    <option value="">Select Status</option>
                    {availableStatuses.map(status => (
                      <option key={status.value} value={status.value}>{status.label}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Comments</label>
                  <textarea
                    className="form-control"
                    rows="4"
                    placeholder="Add any relevant comments or notes..."
                    value={statusFormData.comments}
                    onChange={(e) => setStatusFormData({ ...statusFormData, comments: e.target.value })}
                  />
                </div>

                <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
                  <button
                    type="button"
                    onClick={() => setShowStatusModal(false)}
                    className="btn-secondary"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn-primary"
                    disabled={loading}
                  >
                    {loading ? 'Updating...' : 'Update Status'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Report Upload Modal */}
      {showReportModal && selectedCheck && (
        <div className="modal-overlay" onClick={() => setShowReportModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Upload Verification Report</h2>
              <button className="modal-close" onClick={() => setShowReportModal(false)}>×</button>
            </div>

            <div className="modal-body">
              <div style={{ marginBottom: '1rem', padding: '1rem', background: '#f9fafb', borderRadius: '8px' }}>
                <p style={{ margin: 0, fontSize: '0.875rem', color: '#666', marginBottom: '0.5rem' }}>
                  <strong>Candidate:</strong> {selectedCheck.candidate?.firstName} {selectedCheck.candidate?.lastName}
                </p>
                <p style={{ margin: 0, fontSize: '0.875rem', color: '#666' }}>
                  <strong>Scope:</strong> {selectedCheck.scope?.join(', ')}
                </p>
              </div>

              <form onSubmit={handleUploadReport}>
                <div className="form-group">
                  <label className="form-label">Report URL *</label>
                  <input
                    type="url"
                    className="form-control"
                    placeholder="https://..."
                    value={reportFormData.reportUrl}
                    onChange={(e) => setReportFormData({ ...reportFormData, reportUrl: e.target.value })}
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Verification Result *</label>
                  <select
                    className="form-control"
                    value={reportFormData.result}
                    onChange={(e) => setReportFormData({ ...reportFormData, result: e.target.value })}
                    required
                  >
                    {resultOptions.map(option => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Comments</label>
                  <textarea
                    className="form-control"
                    rows="4"
                    placeholder="Add any findings or notes..."
                    value={reportFormData.comments}
                    onChange={(e) => setReportFormData({ ...reportFormData, comments: e.target.value })}
                  />
                </div>

                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={reportFormData.disclaimerAccepted}
                      onChange={(e) => setReportFormData({ ...reportFormData, disclaimerAccepted: e.target.checked })}
                      required
                    />
                    <span style={{ fontSize: '0.875rem' }}>
                      I certify that the information provided is accurate and complete
                    </span>
                  </label>
                </div>

                <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
                  <button
                    type="button"
                    onClick={() => setShowReportModal(false)}
                    className="btn-secondary"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn-primary"
                    disabled={loading}
                  >
                    {loading ? 'Uploading...' : 'Upload Report'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AgencyDashboard;
